import { useDebouncedValue } from '@tanstack/react-pacer/debouncer';
import { useQuery } from '@tanstack/react-query';

import { clientContactCandidatesOptions } from '@/entities/client';
import { SearchCombobox } from '@/shared/ui/search-combobox/search-combobox';

import {
    CONTACT_SEARCH_DEBOUNCE_MS,
    CONTACT_SEARCH_PAGE_SIZE,
} from '../../../config/add-client-contact-search.config';

type Props = {
    clientId: string;
    clientFirstName: string;
    searchTerm: string;
    onSearchTermChange: (value: string) => void;
    isLoadingPerson: boolean;
    onSelectPerson: (personId: string) => void;
};

export function AddClientContactSearch({
    clientId,
    clientFirstName,
    searchTerm,
    onSearchTermChange,
    isLoadingPerson,
    onSelectPerson,
}: Props) {
    const [debouncedSearchTerm] = useDebouncedValue(searchTerm, {
        wait: CONTACT_SEARCH_DEBOUNCE_MS,
    });

    const { data, isError } = useQuery(
        clientContactCandidatesOptions(clientId, debouncedSearchTerm, CONTACT_SEARCH_PAGE_SIZE),
    );

    // The previous term's answer stays up while the next one loads, but nothing is shown once the
    // field is empty again (FR-011).
    const candidates = searchTerm.trim().length > 0 ? (data?.items ?? []) : [];

    return (
        <SearchCombobox
            variant="navigation"
            layout="dropdown"
            label="Search contacts and clients"
            items={candidates}
            getKey={(candidate) => candidate.personId}
            getLabel={(candidate) => `${candidate.firstName} ${candidate.lastName}`}
            isItemDisabled={(candidate) => candidate.alreadyContact}
            renderItemSuffix={(candidate) =>
                candidate.alreadyContact ? ` (already ${clientFirstName}'s contact)` : null
            }
            searchTerm={searchTerm}
            onSearchTermChange={onSearchTermChange}
            hasData={data !== undefined}
            isError={isError}
            errorMessage="Search failed. Try again."
            disabled={isLoadingPerson}
            onSelect={(candidate) => onSelectPerson(candidate.personId)}
            onClear={() => onSearchTermChange('')}
        />
    );
}
